"use client";

import * as React from "react";
import { LayoutGrid, List, Search, X } from "lucide-react";
import type { Song } from "@gerama/types";
import { cn } from "@gerama/utils";
import { SongRow } from "@/features/songs/song-row";
import { SongCard } from "@/features/songs/song-card";
import { songToTrack } from "@/features/player/to-track";
import { CardGrid } from "./shelf";

export type SongView = "list" | "grid";

const VIEW_KEY = "gerama.songs.view";

/** List / grid preference, remembered per browser. */
export function useSongView(): [SongView, (view: SongView) => void] {
  const [view, setView] = React.useState<SongView>("list");
  React.useEffect(() => {
    try {
      const saved = window.localStorage.getItem(VIEW_KEY);
      if (saved === "grid" || saved === "list") setView(saved);
    } catch {
      // storage blocked — default list
    }
  }, []);
  const set = React.useCallback((next: SongView) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // still applies for this page
    }
  }, []);
  return [view, set];
}

/** Title row of a songs page: the title plus search, grid and list controls (Radio Javan's Trending Songs header). */
export function SongListHeader({
  title,
  view,
  onView,
  search,
  onSearch,
  level = "h1",
}: {
  title: string;
  /** `h2` when the page already has its own `h1`. */
  level?: "h1" | "h2";
  view: SongView;
  onView: (view: SongView) => void;
  search?: string;
  onSearch?: (value: string) => void;
}) {
  const [searching, setSearching] = React.useState(!!search);
  return (
    <div className="flex items-center gap-3">
      {level === "h1" ? (
        <h1 className="me-auto text-3xl font-bold">{title}</h1>
      ) : (
        <h2 className="me-auto text-2xl font-bold">{title}</h2>
      )}
      {onSearch &&
        (searching ? (
          <div className="bg-muted flex h-9 items-center gap-2 rounded-full px-3">
            <Search className="text-muted-foreground size-4" />
            <input
              autoFocus
              value={search ?? ""}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="جستجو در فهرست…"
              aria-label="جستجو در فهرست"
              className="w-40 bg-transparent text-sm outline-none sm:w-56"
            />
            <button
              type="button"
              aria-label="بستن جستجو"
              onClick={() => {
                onSearch("");
                setSearching(false);
              }}
            >
              <X className="text-muted-foreground size-4" />
            </button>
          </div>
        ) : (
          <IconToggle label="جستجو" active={false} onClick={() => setSearching(true)}>
            <Search className="size-5" />
          </IconToggle>
        ))}
      <IconToggle label="نمای شبکه‌ای" active={view === "grid"} onClick={() => onView("grid")}>
        <LayoutGrid className="size-5" />
      </IconToggle>
      <IconToggle label="نمای فهرستی" active={view === "list"} onClick={() => onView("list")}>
        <List className="size-5" />
      </IconToggle>
    </div>
  );
}

function IconToggle({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "flex size-9 items-center justify-center rounded-full transition-colors",
        active ? "text-foreground bg-foreground/10" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

/** Songs as a numbered table (SONG · TIME) or a cover grid. */
export function SongList({
  songs,
  view,
  contextLabel,
  contextHref,
  numbered = true,
}: {
  songs: Song[];
  view: SongView;
  contextLabel: string;
  contextHref?: string;
  numbered?: boolean;
}) {
  const queue = React.useMemo(() => songs.map(songToTrack), [songs]);
  if (view === "grid") {
    return (
      <CardGrid>
        {songs.map((song) => (
          <SongCard
            key={song.id}
            song={song}
            queue={queue}
            contextLabel={contextLabel}
            contextHref={contextHref}
          />
        ))}
      </CardGrid>
    );
  }
  return (
    <div>
      <div className="text-muted-foreground border-border/60 flex items-center gap-3 border-b px-2 pb-2 text-[11px] font-bold tracking-widest">
        <span className="w-8 shrink-0 text-center">#</span>
        <span className="flex-1 ps-[3.25rem]">آهنگ</span>
        <span className="me-10">زمان</span>
      </div>
      <div className="pt-1">
        {songs.map((song, i) => (
          <SongRow
            key={song.id}
            song={song}
            index={numbered ? i : undefined}
            queue={queue}
            contextLabel={contextLabel}
            contextHref={contextHref}
          />
        ))}
      </div>
    </div>
  );
}
